
import { Component, Vue } from 'vue-property-decorator';
import { getPost, addComment, cancelPost } from '@/api/irPost';
import { getCompanyName } from '@/api/company';
import { getUserInfo } from '@/api/user';
import { UserModule } from '@/store/user';
import type { IIrPostDetail, IIrPostCommentCreateRequest, IIrPostCancelRequest } from '@/types/irPost';
import moment from 'moment';

@Component({
  name: 'IrPostDetail',
})
export default class extends Vue {
  /* //////////// */
  private subVisualStyleEl: HTMLStyleElement | null = null;

   private originalSubVisualHTML: string | null = null;

   mounted(): void {
     this.injectSubVisualStyle();
     this.injectSubVisualContent();
     window.addEventListener('beforeunload', this.handleBeforeUnload);
   }

   beforeDestroy(): void {
     this.removeSubVisualStyle();
     this.restoreSubVisualContent();
     window.removeEventListener('beforeunload', this.handleBeforeUnload);
   }

   private injectSubVisualStyle(): void {
     const style = document.createElement('style');
     style.setAttribute('data-from', 'IrPostList');

     style.textContent = `
     .sub_visual.sub_visual01 {
       display: block !important;
       height: auto !important;
       height: 160px !important;
       padding: 20px !important;
       background: none !important;
       overflow: visible !important;
       position: relative !important;
       visibility: visible !important;
       opacity: 1 !important;
     }

     .sub_visual::before,
     .sub_visual::after,
     .sub_visual01::before,
     .sub_visual01::after {
       content: none !important;
       display: none !important;
     }

     #sub_lnb,
     #sub_lnb * {
       color: #000 !important;
       opacity: 1 !important;
       visibility: visible !important;

       -webkit-text-fill-color: #000 !important;
       text-shadow: none !important;
       mix-blend-mode: normal !important;
       filter: none !important;
     }

     #sub_lnb {
       display: block !important;
       position: relative !important;
       z-index: 10 !important;
       background: #fff !important;
       padding: 10px 0;
     }

     .sub_lnb_navi {text-align:right;}
     `;

     document.head.appendChild(style);
     this.subVisualStyleEl = style;
   }

   private removeSubVisualStyle(): void {
     if (this.subVisualStyleEl) {
       document.head.removeChild(this.subVisualStyleEl);
       this.subVisualStyleEl = null;
     }
   }

   private injectSubVisualContent(): void {
     this.$nextTick(() => {
       const subVisual = document.querySelector('.sub_visual');

       if (!subVisual) {
         setTimeout(() => this.injectSubVisualContent(), 50);
         return;
       }

       this.originalSubVisualHTML = subVisual.innerHTML;

       subVisual.innerHTML = `
         <div id="sub_lnb">
           <div class="sub_lnb_box">
             <div class="sub_lnb_ttl">IR매칭</div>
             <div class="sub_lnb_navi">
               <ul>
                 <li>HOME</li>
                 <li>마이페이지</li>
                 <li>IR매칭</li>
               </ul>
             </div>
           </div>
         </div>
       `;
     });
   }

   private restoreSubVisualContent(): void {
     const subVisual = document.querySelector('.sub_visual');

     if (subVisual && this.originalSubVisualHTML !== null) {
       subVisual.innerHTML = this.originalSubVisualHTML;
       this.originalSubVisualHTML = null;
     }
   }
   /*  ////////// */

  private post: IIrPostDetail | null = null;

  private companyName = '';

  private fromCompanyName = '';

  private newCommentContent = '';

  private cancelReason = '';

  private isCancelFormVisible = false;

  private isLoading = true;

  private isSubmitting = false;

  private currentLoggedInUserUid: string | null = null;

  get sortedComments(): any[] {
    if (this.post && this.post.comments) {
      return [...this.post.comments]
        .sort((a: any, b: any) => moment(a.createdDate).valueOf() - moment(b.createdDate).valueOf())
        .map((comment: any) => {
          let avatarChar = '';
          if (this.post && comment.userUid === this.post.fromUserUid) {
            avatarChar = 'Q';
          } else if (this.post && comment.userUid === this.post.toUserUid) {
            avatarChar = 'A';
          } else {
            avatarChar = comment.companyName.charAt(0).toUpperCase();
          }
          return { ...comment, avatarChar };
        });
    }
    return [];
  }

  get shouldDisableCommentForm(): boolean {
    if (this.post) {
      const { status } = this.post;
      return ['D', 'E', 'F'].includes(status);
    }
    return false;
  }

  get showCancelButton(): boolean {
    if (this.post && this.currentLoggedInUserUid !== null) {
      const canCancel = !['D', 'E', 'F', 'S'].includes(this.post.status);
      const isAuthorizedUser = (
        this.currentLoggedInUserUid === this.post.fromUserUid
          || this.currentLoggedInUserUid === this.post.toUserUid
      );
      return isAuthorizedUser && canCancel;
    }
    return false;
  }

  get postId(): string {
    return this.$route.params.postUid;
  }

  private async fetchPost(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await getPost(this.postId);
      this.post = response.data;

      if (this.post) {
        // ✅ 게시물 작성자 회사명 조회
        if (this.post.toUserUid) {
          const companyResponse = await getCompanyName(this.post.toUserUid);
          this.companyName = companyResponse.data.coName;
        }

        if (this.post.fromUserUid) {
          const companyResponse = await getCompanyName(this.post.fromUserUid);
          this.fromCompanyName = companyResponse.data.coName;
        }

        // ✅ 메시지 작성자들의 회사명 조회
        if (this.post.comments && this.post.comments.length > 0) {
          const enrichedComments = await Promise.all(
            this.post.comments.map(async (comment: any) => {
              try {
                const res = await getCompanyName(comment.userUid);
                return { ...comment, companyName: res.data.coName };
              } catch (error) {
                console.error(`회사 정보 조회 실패 (${comment.userUid}):`, error);
                return { ...comment, companyName: comment.userUid }; // 실패 시 UID 그대로 표시
              }
            }),
          );
          this.post.comments = enrichedComments;
        }
      }
    } catch (error) {
      console.error('게시글 상세 정보 조회 실패:', error);
      this.post = null;
    } finally {
      this.isLoading = false;
    }
  }

  private async handleCancelPost(): Promise<void> {
    if (!this.cancelReason.trim()) {
      alert('취소 사유를 입력해주세요.');
      return;
    }
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      const cancelData: IIrPostCancelRequest = {
        cancelReason: this.cancelReason,
        status: 'F',
      };
      await cancelPost(this.postId, cancelData);

      // API 호출 성공 후 연락처 정보 출력 및 SMS 발송
      const recipient = this.getRecipientInfo();
      if (recipient && this.currentLoggedInUserUid) {
        // console.log('상대방 UID (취소 성공 시):', recipient.uid);
        // console.log('상대방 연락처 (취소 성공 시):', recipient.phone);

        try {
          // Get company names for SMS payload
          const senderCompany = (await getCompanyName(this.currentLoggedInUserUid)).data.coName;
          const recipientCompany = (await getCompanyName(recipient.uid)).data.coName;

          await this.sendSms(
            recipient.phone,
            moment().format('YYYY-MM-DD'),
            senderCompany,
            recipientCompany,
            'F', // As requested by user
          );
        } catch (smsError) {
          console.error('❌ SMS 서비스 호출 중 오류:', smsError);
        }
      } else {
        console.warn('상대방 정보를 찾을 수 없습니다. (취소 성공 시)');
      }

      // console.log('IR 매칭이 성공적으로 취소되었습니다.');
      this.isCancelFormVisible = false;
      this.cancelReason = '';
      await this.fetchPost();
    } catch (error) {
      console.error('IR 매칭 취소 실패:', error);
      alert('IR 매칭 취소에 실패했습니다. 다시 시도해주세요.');
    } finally {
      this.isSubmitting = false;
    }
  }

  private getRecipientInfo(): { uid: string, phone: string } | null {
    if (this.post && this.currentLoggedInUserUid) {
      if (this.currentLoggedInUserUid === this.post.fromUserUid) {
        return { uid: this.post.toUserUid, phone: this.post.toUserPhone || '' };
      }
      if (this.currentLoggedInUserUid === this.post.toUserUid) {
        return { uid: this.post.fromUserUid, phone: this.post.fromUserPhone || '' };
      }
    }
    return null;
  }

  // SMS 발송 헬퍼 함수
  private async sendSms(
    recipientPhone: string,
    irDate: string,
    irCo1: string,
    irCo2: string,
    irTp: string,
  ): Promise<void> {
    const smsData = new FormData();
    smsData.append('irp', recipientPhone);
    smsData.append('irdate', irDate);
    smsData.append('ircoA', irCo1);
    smsData.append('ircoB', irCo2);
    smsData.append('irtp', irTp);

    try {
      const smsResponse = await fetch('https://www.azen.co.kr/sms_service/sms_service_gnst.php', {
        method: 'POST',
        body: smsData,
      });

      const responseText = await smsResponse.text();
    } catch (smsError) {
      console.error(`❌ SMS 서비스 호출 중 오류 (${recipientPhone}):`, smsError);
    }
  }

  private async handleCommentSubmit(): Promise<void> {
    if (!this.newCommentContent.trim()) {
      console.warn('메시지 내용을 입력해주세요.');
      return;
    }
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      const commentData: IIrPostCommentCreateRequest = {
        postId: this.postId,
        content: this.newCommentContent,
      };
      await addComment(commentData);
      // console.log('메시지이 성공적으로 등록되었습니다.');

      // API 호출 성공 후 연락처 정보 출력 및 SMS 발송
      const recipient = this.getRecipientInfo();
      if (recipient && recipient.phone && this.currentLoggedInUserUid) {
        // console.log('상대방 UID (메시지 등록 성공 시):', recipient.uid);
        // console.log('상대방 연락처 (메시지 등록 성공 시):', recipient.phone);

        try {
          // Get company names for SMS payload
          const senderCompany = (await getCompanyName(this.currentLoggedInUserUid)).data.coName;
          const recipientCompany = (await getCompanyName(recipient.uid)).data.coName;

          await this.sendSms(
            recipient.phone,
            moment().format('YYYY-MM-DD'),
            senderCompany,
            recipientCompany,
            'C', // As requested by user
          );
        } catch (smsError) {
          console.error('❌ SMS 서비스 호출 중 오류:', smsError);
        }
      } else {
        console.warn('상대방 정보를 찾을 수 없습니다.');
      }

      this.newCommentContent = '';
      await this.fetchPost();
    } catch (error) {
      console.error('메시지 생성 실패:', error);
      console.error('메시지 등록에 실패했습니다. 다시 시도해주세요.', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      A: '승인 대기',
      B: '답변 대기',
      C: '진행중',
      D: '관리자 반려',
      E: '투자사 거절',
      F: 'IR매칭 취소',
      S: 'IR매칭 완료',
    };
    return statusMap[status] || status;
  }

  private getStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      A: 'info',
      B: 'warning',
      C: 'primary',
      D: 'danger',
      E: 'danger',
      F: 'info',
      S: 'success',
    };
    return statusMap[status] || 'info';
  }

  private formatDate(date: string): string {
    return moment(date).format('YYYY-MM-DD HH:mm');
  }

  private goToList(): void {
    this.$router.push({
      name: 'Chat',
      params: {
        // menuUid: 'f3e3cdbd-b57b-40fe-ad1a-f55d55ee4a12', // 메뉴 UID(로컬)
        menuUid: 'da5150a8-c524-4997-a73e-194447f8e686', // 메뉴 UID
      },
    });
  }

  private handleBeforeUnload(event: BeforeUnloadEvent): string | undefined {
    if (this.newCommentContent.trim()) {
      event.preventDefault();
      // 최신 브라우저는 자체 내장 메시지를 표시합니다.
      // 경고창을 띄우려면 returnValue에 값을 할당해야 합니다.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
      return '';
    }
    return undefined;
  }

  private async created(): Promise<void> {
    await this.fetchPost();
    // Fetch current user's UID
    if (UserModule.token) {
      try {
        const uRes = await getUserInfo();
        this.currentLoggedInUserUid = uRes.data.uid;
      } catch (error) {
        console.error('사용자 정보를 불러오는데 실패했습니다.', error);
      }
    } else {
      console.warn('사용자 토큰이 없습니다. 로그인되지 않은 상태입니다.');
    }
  }
}
